import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronBackward, ChevronForward } from '@naf-simba/building-blocks-icons/Arrows';
import { YoutubeShortsBackgroundType } from '../../../../types/YoutubeShortsBlockType';
import { CARD_WIDTH, CARD_GAP, getBackgroundColor, getEmbedUrl } from './ShortsCarousel.utils';
import * as S from './ShortsCarousel.styles';

export type ShortVideo = {
  id: string;
  title?: string | null;
};

export type ShortsCarouselProps = {
  videos?: ShortVideo[] | null;
  title?: string | null;
  background?: YoutubeShortsBackgroundType;
};

export const ShortsCarousel = ({ videos, title, background = 'primary' }: ShortsCarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shortVideos = useMemo(() => videos?.filter((video): video is ShortVideo => Boolean(video?.id)) ?? [], [videos]);
  const backgroundColor = getBackgroundColor(background);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(shortVideos.length > 2);
  const [activeIndex, setActiveIndex] = useState(0);
  const isSingleVideo = shortVideos.length === 1;
  const shouldShowArrows = shortVideos.length > 2;

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container || shortVideos.length === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setActiveIndex(0);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);

    const cardWidthWithGap = CARD_WIDTH + CARD_GAP;
    const newIndex = Math.round(scrollLeft / cardWidthWithGap);
    setActiveIndex(Math.min(newIndex, shortVideos.length - 1));

    if (shortVideos.length <= 2) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setActiveIndex(0);
    }
  }, [shortVideos.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollToIndex = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidthWithGap = CARD_WIDTH + CARD_GAP;
    container.scrollTo({
      left: index * cardWidthWithGap,
      behavior: 'smooth',
    });
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToIndex(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < shortVideos.length - 1) {
      scrollToIndex(activeIndex + 1);
    }
  };

  if (shortVideos.length === 0) return null;

  return (
    <S.OuterWrapper>
      <S.CarouselContainer $backgroundColor={backgroundColor} $hasTitle={!!title}>
        <S.InternalContainer>
          {title && <S.CarouselTitle className="shortsTitle">{title}</S.CarouselTitle>}
          <S.CarouselWrapper $isSingleVideo={isSingleVideo}>
            {shouldShowArrows && canScrollLeft && (
              <S.StyledArrowLeft
                onClick={handlePrevious}
                aria-label="Forrige video"
                icon={ChevronBackward}
                color="secondary"
              />
            )}
            <S.ScrollContainer ref={scrollContainerRef} $isSingleVideo={isSingleVideo}>
              {shortVideos.map((video, index) => (
                <S.VideoCard key={video.id} $isActive={index === activeIndex}>
                  <S.VideoIframe
                    src={getEmbedUrl(video)}
                    title={video.title || `YouTube Short ${index + 1}`}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </S.VideoCard>
              ))}
            </S.ScrollContainer>
            {shouldShowArrows && canScrollRight && (
              <S.StyledArrowRight
                onClick={handleNext}
                aria-label="Neste video"
                icon={ChevronForward}
                color="secondary"
              />
            )}
          </S.CarouselWrapper>
        </S.InternalContainer>
      </S.CarouselContainer>
    </S.OuterWrapper>
  );
};

export default ShortsCarousel;
